import * as THREE from 'three';
import { Capsule } from 'three/examples/jsm/math/Capsule.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { AnimationMixer, Group, Scene } from 'three';
import type { Octree } from 'three/examples/jsm/math/Octree.js';

type AnimationMap = Map<string, THREE.AnimationAction>;
type PlayerPosition = { x: number; y: number; z: number };
type PlayerRotation = { x: number; y: number; z: number };
export class PlayerController {
  private scene: Scene;
  private worldOctree: Octree;
  private model: Group | null = null;
  private mixer: AnimationMixer | null = null;
  private animationsMap: AnimationMap = new Map();
  private initialPosition: THREE.Vector3;
  private initialRotation: THREE.Vector3;
  private playerVelocity: THREE.Vector3 = new THREE.Vector3();
  private playerOnFloor = true;
  private gravity = 30;
  private runVelocity = 5;
  private walkVelocity = 2;
  private toggleRun = true;
  private currentAction = 'Idle';
  private fadeDuration = 0.2;
  private walkDirection: THREE.Vector3 = new THREE.Vector3();
  private rotateAngle: THREE.Vector3 = new THREE.Vector3(0, 1, 0);
  private rotateQuarternion: THREE.Quaternion = new THREE.Quaternion();
  private maxVelocity = 50;
  private maxPositionChange = 5;
  private previousPosition: THREE.Vector3 = new THREE.Vector3();
  private velocityHistory: THREE.Vector3[] = [];
  private maxVelocityHistorySize = 5;
  private sceneLoadComplete = false;
  private playerCollider: Capsule;

  constructor(
    scene: Scene,
    worldOctree: Octree,
    avatarInfo: { player?: { position?: PlayerPosition; rotation?: PlayerRotation } },
  ) {
    this.scene = scene;
    this.worldOctree = worldOctree;
    // 保存初始位置
    this.initialPosition = avatarInfo?.player?.position
      ? new THREE.Vector3(avatarInfo.player.position.x, avatarInfo.player.position.y, avatarInfo.player.position.z)
      : new THREE.Vector3(0, 0, 0);
    this.initialRotation = avatarInfo?.player?.rotation
      ? new THREE.Vector3(avatarInfo.player.rotation.x, avatarInfo.player.rotation.y, avatarInfo.player.rotation.z)
      : new THREE.Vector3(0, 0, 0);
    // 物理属性
    this.playerVelocity = new THREE.Vector3();
    this.playerOnFloor = true; // 初始设置为true，防止掉落
    this.gravity = 30;
    // 移动属性
    this.runVelocity = 5;
    this.walkVelocity = 2;
    this.toggleRun = true;
    // 动画属性
    this.currentAction = 'Idle';
    this.fadeDuration = 0.2;
    // 临时计算变量
    this.walkDirection = new THREE.Vector3();
    this.rotateAngle = new THREE.Vector3(0, 1, 0);
    this.rotateQuarternion = new THREE.Quaternion();
    // 碰撞异常检测和过滤参数
    this.maxVelocity = 50; // 最大允许速度
    this.maxPositionChange = 5; // 单帧最大位置变化
    this.previousPosition = new THREE.Vector3();
    this.velocityHistory = []; // 速度历史记录
    this.maxVelocityHistorySize = 5; // 保留最近5帧的速度记录
    this.sceneLoadComplete = false;
    // 初始化碰撞体 - 确保起始位置在地面上
    this.playerCollider = new Capsule(
      new THREE.Vector3(this.initialPosition.x, this.initialPosition.y + 0.35, this.initialPosition.z),
      new THREE.Vector3(this.initialPosition.x, this.initialPosition.y + 1, this.initialPosition.z),
      0.35,
    );

    // 确保角色在地面上
    this.ensurePlayerOnGround();

    // 初始化previousPosition
    this.previousPosition.copy(this.playerCollider.end);
  }

  // 重置异常检测状态
  resetAbnormalDetection() {
    this.velocityHistory = [];
    this.playerVelocity.set(0, 0, 0);
    this.previousPosition.copy(this.playerCollider.end);
    console.log('异常检测状态已重置');
  }

  async init(): Promise<void> {
    await this.loadCharacterModel();
    console.log('loadCharacterModel', this.initialPosition);
    // 保存初始位置
    // this.setPositionAndRotation(this.initialPosition, this.initialRotation);
  }

  loadCharacterModel(): Promise<this> {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      loader.setPath('./models/');
      loader.load(
        'Soldier.glb',
        (gltf) => {
          this.model = gltf.scene;
          this.model.traverse((object: any) => {
            // eslint-disable-next-line no-param-reassign
            if (object.isMesh) object.castShadow = true;
          });
          // 设置模型的初始位置
          if (this.initialPosition) {
            this.model.position.copy(this.initialPosition);
            // 更新碰撞体位置以匹配模型位置
            this.playerCollider.start.set(
              this.initialPosition.x,
              this.initialPosition.y + 0.35,
              this.initialPosition.z,
            );
            this.playerCollider.end.set(this.initialPosition.x, this.initialPosition.y + 1, this.initialPosition.z);
          }
          if (this.initialRotation) {
            this.model.rotation.x = this.initialRotation.x;
            this.model.rotation.y = this.initialRotation.y;
            this.model.rotation.z = this.initialRotation.z;
          }
          this.scene.add(this.model);
          const gltfAnimations = gltf.animations;
          this.mixer = new THREE.AnimationMixer(this.model);
          gltfAnimations
            .filter((a) => a.name !== 'TPose')
            .forEach((a) => {
              this.animationsMap.set(a.name, this.mixer!.clipAction(a));
            });
          // 播放初始动画
          this.animationsMap.forEach((value, key) => {
            if (key === this.currentAction) {
              value.play();
            }
          });
          resolve(this);
        },
        undefined,
        reject,
      );
    });
  }

  isLoaded(): boolean {
    return this.model !== null && this.mixer !== null;
  }

  switchRunToggle(): void {
    this.toggleRun = !this.toggleRun;
  }

  // 确保角色在地面上的方法
  ensurePlayerOnGround(): void {
    // 创建一个向下的射线，检测地面
    const startPosition = new THREE.Vector3(0, 10, 0); // 从稍高的位置开始
    // const rayDirection = new THREE.Vector3(0, -1, 0); // 向下的方向

    // 将碰撞体移动到起始位置
    this.playerCollider.start.copy(startPosition);
    this.playerCollider.end.copy(new THREE.Vector3(0, startPosition.y + 0.65, 0));
    // 应用一个小的向下速度，让物理系统将角色放置在地面上
    this.playerVelocity.set(0, -0.1, 0);
  }

  update(delta: number, inputController: any, camera: THREE.Camera): void {
    if (!this.mixer) return; // 确保模型已加载
    const directionPressed = inputController.isDirectionPressed();

    // 检查跑步状态（键盘Shift或移动端按钮）
    // 移动端按钮状态优先，如果没有移动端输入则使用键盘状态
    const mobileRunState = inputController.getRunToggleState();
    const shouldRun = inputController.mobileController.isMobileDevice() ? mobileRunState : this.toggleRun;
    // 确定要播放的动画
    let play = '';
    if (directionPressed && shouldRun) {
      play = 'Run';
    } else if (directionPressed) {
      play = 'Walk';
    } else {
      play = 'Idle';
    }
    // 切换动画
    if (this.currentAction !== play) {
      const toPlay = this.animationsMap.get(play);
      const current = this.animationsMap.get(this.currentAction);
      if (toPlay && current) {
        current.fadeOut(this.fadeDuration);
        toPlay.reset().fadeIn(this.fadeDuration).play();
        this.currentAction = play;
      }
    }
    this.mixer.update(delta);
    // 应用重力 - 但确保初始状态不会掉落
    if (this.playerOnFloor) {
      this.playerVelocity.y = Math.max(0, this.playerVelocity.y);
    } else {
      this.playerVelocity.y -= this.gravity * delta;
    }
    // 处理移动输入
    if (this.currentAction === 'Run' || this.currentAction === 'Walk') {
      this.handleMovement(inputController, camera);
    } else {
      // 停止时的阻尼
      this.playerVelocity.x -= this.playerVelocity.x * 10.0 * delta;
      this.playerVelocity.z -= this.playerVelocity.z * 10.0 * delta;
    }
    // 更新玩家位置和碰撞检测
    this.updatePosition(delta);
  }

  handleMovement(inputController: any, camera: THREE.Camera): void {
    // 计算相对于摄像机的角度
    const angleYCameraDirection = Math.atan2(
      camera.position.x - this.model!.position.x,
      camera.position.z - this.model!.position.z,
    );
    // 获取方向偏移
    const directionOffset = inputController.getDirectionOffset();
    // 旋转模型
    this.rotateQuarternion.setFromAxisAngle(this.rotateAngle, angleYCameraDirection + directionOffset);
    this.model!.quaternion.rotateTowards(this.rotateQuarternion, 0.2);
    // 计算移动方向
    camera.getWorldDirection(this.walkDirection);
    this.walkDirection.y = 0;
    this.walkDirection.normalize();
    this.walkDirection.applyAxisAngle(this.rotateAngle, directionOffset);
    // 设置速度
    const velocity = this.currentAction === 'Run' ? this.runVelocity : this.walkVelocity;
    this.playerVelocity.x = this.walkDirection.x * velocity;
    this.playerVelocity.z = this.walkDirection.z * velocity;
  }
  // 检测并过滤异常的碰撞值
  filterAbnormalCollision(result: any): any {
    if (!result) return result;
    // 检查碰撞深度是否异常
    if (result.depth > this.maxPositionChange) {
      console.warn(`检测到异常碰撞深度: ${result.depth.toFixed(3)}, 限制为: ${this.maxPositionChange}`);
      // eslint-disable-next-line no-param-reassign
      result.depth = Math.min(result.depth, this.maxPositionChange);
    }
    // 检查法向量是否合理
    if (result.normal.length() < 0.1 || result.normal.length() > 1.1) {
      // console.warn('检测到异常法向量，使用默认向上法向量');
      result.normal.set(0, 1, 0);
    }
    return result;
  }
  // 限制速度变化
  limitVelocityChange(newVelocity: THREE.Vector3): THREE.Vector3 {
    // 记录速度历史
    this.velocityHistory.push(newVelocity.clone());
    if (this.velocityHistory.length > this.maxVelocityHistorySize) {
      this.velocityHistory.shift();
    }
    // 检查速度是否异常
    const velocityMagnitude = newVelocity.length();
    if (velocityMagnitude > this.maxVelocity) {
      console.warn(`检测到异常速度: ${velocityMagnitude.toFixed(3)}, 限制为: ${this.maxVelocity}`);
      newVelocity.normalize().multiplyScalar(this.maxVelocity);
    }
    // 检查速度变化是否过于剧烈
    if (this.velocityHistory.length >= 2) {
      const prevVelocity = this.velocityHistory[this.velocityHistory.length - 2];
      const velocityChange = newVelocity.clone().sub(prevVelocity);
      const maxVelocityChange = this.maxVelocity * 2; // 允许的最大速度变化
      if (velocityChange.length() > maxVelocityChange) {
        console.warn(`检测到异常速度变化: ${velocityChange.length().toFixed(3)}, 使用平滑过渡`);
        // 使用平滑过渡而不是突变
        const smoothFactor = 0.3;
        newVelocity.lerp(prevVelocity, 1 - smoothFactor);
      }
    }
    return newVelocity;
  }
  setSceneLoadComplete() {
    this.sceneLoadComplete = true;
  }

  updatePosition(delta: number): void {
    if (!this.model || !this.sceneLoadComplete) return; // 确保模型已加载
    // 记录当前位置用于异常检测
    this.previousPosition.copy(this.playerCollider.end);
    // 限制速度变化
    this.playerVelocity = this.limitVelocityChange(this.playerVelocity);
    const deltaPosition = this.playerVelocity.clone().multiplyScalar(delta);
    // 检查位置变化是否异常
    if (deltaPosition.length() > this.maxPositionChange) {
      console.warn(`检测到异常位置变化: ${deltaPosition.length().toFixed(3)}, 限制为: ${this.maxPositionChange}`);
      deltaPosition.normalize().multiplyScalar(this.maxPositionChange);
    }
    this.playerCollider.translate(deltaPosition);
    // 碰撞检测
    this.playerOnFloor = false;
    let result = this.worldOctree.capsuleIntersect(this.playerCollider);
    if (result) {
      // 过滤异常的碰撞检测结果
      result = this.filterAbnormalCollision(result);
      this.playerOnFloor = result.normal.y > 0;
      if (!this.playerOnFloor) {
        const velocityAdjustment = result.normal.clone().multiplyScalar(-result.normal.dot(this.playerVelocity));
        // 限制速度调整的幅度，防止过度反弹
        if (velocityAdjustment.length() > this.maxVelocity * 0.5) {
          velocityAdjustment.normalize().multiplyScalar(this.maxVelocity * 0.5);
        }
        this.playerVelocity.add(velocityAdjustment);
      }
      // 限制碰撞修正的距离
      const correctionVector = result.normal.multiplyScalar(result.depth);
      if (correctionVector.length() > this.maxPositionChange * 0.5) {
        correctionVector.normalize().multiplyScalar(this.maxPositionChange * 0.5);
      }
      this.playerCollider.translate(correctionVector);
    }
    // 未初始化完成 防止角色掉到地面以下
    if (!this.sceneLoadComplete && this.playerCollider.end.y < this.initialPosition[1]) {
      this.playerCollider.end.y = -this.initialPosition[1];
      this.playerCollider.start.y = -this.initialPosition[1] - 0.65;
      this.playerOnFloor = true;
      // eslint-disable-next-line prefer-destructuring
      this.playerVelocity.y = this.initialPosition[1];
    }
    // 最终位置安全检查
    const finalPositionChange = this.playerCollider.end.clone().sub(this.previousPosition);
    if (finalPositionChange.length() > this.maxPositionChange) {
      console.warn('最终位置变化异常，回滚到安全位置');
      // 回滚到之前的位置，但允许小幅移动
      const safeMovement = finalPositionChange.normalize().multiplyScalar(this.maxPositionChange * 0.5);
      this.playerCollider.end.copy(this.previousPosition).add(safeMovement);
      this.playerCollider.start.copy(this.playerCollider.end).add(new THREE.Vector3(0, -0.65, 0));
      // 重置异常速度
      this.playerVelocity.multiplyScalar(0.5);
    }
    // 更新模型位置
    this.model.position.copy(this.playerCollider.end);
    this.model.position.y -= 1; // 调整角色高度
    // 记录当前位置，可用于保存玩家位置状态
    this.initialPosition.copy(this.model.position);
  }

  getPosition(): THREE.Vector3 {
    return this.model ? this.model.position.clone() : new THREE.Vector3();
  }

  getVelocity(): THREE.Vector3 {
    return this.playerVelocity.clone();
  }

  getRotation(): THREE.Euler {
    return this.model ? this.model.rotation.clone() : new THREE.Euler();
  }

  setPosition(x: number, y: number, z: number): void {
    if (!this.model) return;
    // 更新模型位置
    this.model.position.set(x, y, z);
    // 更新碰撞体位置
    this.playerCollider.start.set(x, y + 0.35, z);
    this.playerCollider.end.set(x, y + 1, z);
    // 更新初始位置记录
    this.initialPosition.set(x, y, z);
    // 重置速度，防止意外移动
    this.playerVelocity.set(0, 0, 0);
    // 重置异常检测状态，防止位置跳跃被误判为异常
    this.resetAbnormalDetection();
    console.log('玩家位置已设置为:', { x, y, z });
  }

  setRotation(x: number, y: number, z: number): void {
    if (!this.model) return;
    // 设置模型旋转
    this.model.rotation.set(x, y, z);
    console.log('玩家朝向已设置为:', { x, y, z });
  }
  // 设置玩家位置和朝向的便捷方法
  setPositionAndRotation(position: THREE.Vector3, rotation: THREE.Euler): void {
    position && this.setPosition(position.x, position.y, position.z);
    rotation && this.setRotation(rotation.x, rotation.y, rotation.z);
  }
  /**
   * 保存当前位置和视角信息
   * @param {Object} sceneController - 场景控制器实例
   * @param {string} modelUrl - 模型URL用于存储键
   * @returns {boolean} 保存是否成功
   */
  saveCurrentPosition(sceneController: any, modelUrl = 'default'): boolean {
    if (!this.model || !sceneController) {
      console.warn('PlayerController或SceneController未初始化完成，无法保存位置');
      return false;
    }
    try {
      // 获取玩家位置和朝向
      const playerPosition = this.getPosition();
      const playerRotation = this.getRotation();
      // 获取摄像头信息
      const camera = sceneController.getCamera();
      const orbitControl = sceneController.getOrbitControl();
      const cameraInfo = {
        position: {
          x: camera.position.x,
          y: camera.position.y,
          z: camera.position.z,
        },
        target: {
          x: orbitControl.target.x,
          y: orbitControl.target.y,
          z: orbitControl.target.z,
        },
        polarAngle: orbitControl.getPolarAngle(),
        azimuthalAngle: orbitControl.getAzimuthalAngle(),
        distance: orbitControl.getDistance(),
      };
      // 保存的数据结构
      const saveData = {
        timestamp: Date.now(),
        modelUrl,
        player: {
          position: {
            x: playerPosition.x,
            y: playerPosition.y,
            z: playerPosition.z,
          },
          rotation: {
            x: playerRotation.x,
            y: playerRotation.y,
            z: playerRotation.z,
          },
        },
        camera: cameraInfo,
      };
      // 保存到localStorage
      const storageKey = `metaverse_position_${modelUrl}`;
      localStorage.setItem(storageKey, JSON.stringify(saveData));
      console.log('位置信息已保存:', saveData, JSON.stringify(saveData));
      return true;
    } catch (error) {
      console.error('保存位置信息失败:', error);
      return false;
    }
  }
  /**
   * 恢复保存的位置和视角信息
   * @param {Object} sceneController - 场景控制器实例
   * @param {string} modelUrl - 模型URL用于存储键
   * @param skipCamera
   * @returns {boolean} 恢复是否成功
   */
  restoreSavedPosition(sceneController: any, modelUrl = 'default', skipCamera = false): boolean {
    if (!this.model || !sceneController) {
      console.warn('PlayerController或SceneController未初始化完成，无法恢复位置');
      return false;
    }
    try {
      const storageKey = `metaverse_position_${modelUrl}`;
      const savedDataStr = localStorage.getItem(storageKey);
      if (!savedDataStr) {
        console.log('没有找到保存的位置信息');
        return false;
      }
      const savedData = JSON.parse(savedDataStr);
      console.log('恢复位置信息:', savedData);
      savedData?.player && this.setPositionAndRotation(savedData.player.position, savedData.player.rotation);
      if (!skipCamera && savedData?.camera) {
        // 恢复摄像头信息
        sceneController.setCameraAndOribitControl(savedData.camera);
      }
      return true;
    } catch (error) {
      console.error('恢复位置信息失败:', error);
      return false;
    }
  }

  /**
   * 检查是否有保存的位置信息
   * @param {string} modelUrl - 模型URL用于存储键
   * @returns {boolean} 是否存在保存的位置信息
   */
  hasSavedPosition(modelUrl = 'default'): boolean {
    const storageKey = `metaverse_position_${modelUrl}`;
    return localStorage.getItem(storageKey) !== null;
  }
  /**
   * 清除保存的位置信息
   * @param {string} modelUrl - 模型URL用于存储键
   * @returns {boolean} 清除是否成功
   */
  clearSavedPosition(modelUrl = 'default'): boolean {
    try {
      const storageKey = `metaverse_position_${modelUrl}`;
      localStorage.removeItem(storageKey);
      console.log('已清除保存的位置信息');
      return true;
    } catch (error) {
      console.error('清除位置信息失败:', error);
      return false;
    }
  }
}
